import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase, bootHadFreshAuthLink } from "@/lib/supabaseClient";
import { apiPost } from "@/lib/api";
import { clearOtherUsersData, cachePut, cacheGet, outboxCount, safeQuery, safeAll, setCacheScope, purgeStaleCompanyCaches } from "@/lib/offline";
import { purgeDeviceData } from "@/lib/deviceCleanup";
import { isStandalonePWA, isIdleExpired, markIdleSignout, writeLastActivity, clearLastActivity, rememberIdleRole } from "@/lib/idle";
import { logWarn } from "@/lib/log";

const AuthContext = createContext(undefined);

export const ROLE_LABELS = {
  super_admin: "Platform Super-Admin",
  org_admin: "Org Admin",
  supervisor: "Supervisor",
  field_employee: "Field Employee",
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [features, setFeatures] = useState(null); // null = not yet loaded
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const activateAttempted = useRef(false);
  // Decided SYNCHRONOUSLY at first render — before any Supabase auth event can
  // stamp fresh activity — so reopening a browser past the idle limit always
  // lands on Login. Never true in the installed PWA.
  // Exception (Step 7 carve-out): a recovery/email-change fragment in the boot URL
  // is a just-clicked emailed link — fresh activity, not an idle boot. The flag and
  // activity stamp are set at module load in supabaseClient.js, before the client
  // can strip the hash.
  const bootIdleExpired = useRef(!isStandalonePWA() && !bootHadFreshAuthLink && isIdleExpired());

  const endExpiredSession = useCallback((uid) => {
    markIdleSignout();
    clearLastActivity();
    supabase.auth.signOut().catch(() => {});
    // Step 11 scenario 8: never destroy unsent queued forms without a user
    // confirmation — an expired session signs out but keeps the outbox.
    (async () => {
      const queued = uid ? await outboxCount(uid) : 0;
      if (queued === 0) await purgeDeviceData();
    })().catch(() => {});
    setSession(null);
  }, []);

  const loadData = useCallback(async (uid) => {
    // Different user on the same device: purge the previous user's local
    // drafts/queued forms and SW caches so no data bleeds between accounts.
    try {
      const lastUid = localStorage.getItem("hakina_last_uid");
      if (lastUid && lastUid !== uid) {
        await clearOtherUsersData(uid);
        if ("caches" in window) caches.keys().then((ks) => ks.forEach((k) => caches.delete(k)));
      }
      localStorage.setItem("hakina_last_uid", uid);
    } catch (e) { logWarn("[auth] device-switch cleanup failed:", e); }
    setDataLoading(true);
    const hydrateSnapshot = () => {
      const snap = cacheGet(uid, "auth");
      if (snap) {
        setProfile((p) => p || snap.profile);
        setMemberships((m) => (m.length ? m : snap.memberships || []));
        setCompanies((c) => (Object.keys(c).length ? c : snap.companies || {}));
      }
    };
    try {
      const fetchAll = () => safeAll([
        supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
        supabase.from("memberships").select("*").eq("user_id", uid).order("created_at", { ascending: true }),
        supabase.from("companies").select("id,name,industry,display_name,logo_path,active,idle_limit_minutes"),
      ]);
      const isNetErr = (...rs) => !navigator.onLine ||
        rs.some((r) => /fetch|network/i.test(String(r?.error?.message || "")));
      let [profRes, memRes, compRes] = await fetchAll();
      // Server-side errors right after sign-in (JWT propagation race) → retry
      // with backoff. True NETWORK failures (offline) are not retried.
      for (const delay of [500, 1000, 1600]) {
        if ((!memRes.error && !compRes.error) || isNetErr(memRes, compRes)) break;
        await new Promise((r) => setTimeout(r, delay));
        [profRes, memRes, compRes] = await fetchAll();
      }
      // A failed fetch is NOT an answer: never wipe known-good state or misread
      // it as deactivated/unassigned. Keep state, or hydrate the last-known-good
      // snapshot (PWA rebooted offline).
      if (memRes.error || compRes.error) {
        hydrateSnapshot();
        return;
      }
      let prof = profRes.data;
      let mem = memRes.data;
      const comps = compRes.data;
      // First-login auto-activation: a pending-only invitee is activated with
      // the role their admin pre-selected, then their memberships are re-fetched.
      // Never on the accept-invite page — activation happens after the password is set.
      const hasActive = (mem || []).some((m) => m.status === "active");
      const hasPending = (mem || []).some((m) => m.status === "pending");
      const onInvitePage = window.location.pathname.startsWith("/accept-invite");
      if (!hasActive && hasPending && !activateAttempted.current && !onInvitePage) {
        activateAttempted.current = true;
        try {
          await apiPost("/onboarding/activate");
          const { data: mem2 } = await supabase.from("memberships").select("*").eq("user_id", uid).order("created_at", { ascending: true });
          mem = mem2 || mem;
        } catch (e) {
          activateAttempted.current = false; // allow retry on next load if it failed
        }
      }
      setProfile(prof || null);
      setMemberships(mem || []);
      const map = {};
      (comps || []).forEach((c) => (map[c.id] = c));
      // RLS/JWT propagation can transiently return zero companies right after
      // sign-in; retry with backoff before the UI could misread this as "deactivated".
      const activeMem = (mem || []).find((m) => m.status === "active");
      if (activeMem && !map[activeMem.company_id]) {
        for (const delay of [400, 800, 1600]) {
          await new Promise((r) => setTimeout(r, delay));
          const { data: comps2 } = await supabase.from("companies").select("id,name,industry,display_name,logo_path,active,idle_limit_minutes");
          (comps2 || []).forEach((c) => (map[c.id] = c));
          if (map[activeMem.company_id]) break;
        }
      }
      setCompanies(map);
      cachePut(uid, "auth", { profile: prof || null, memberships: mem || [], companies: map });
    } catch (e) {
      // A thrown fetch (airplane mode severing requests) must hydrate too.
      logWarn("[auth] loadData threw — hydrating snapshot:", e);
      hydrateSnapshot();
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      if (data.session?.user && bootIdleExpired.current) {
        bootIdleExpired.current = false;
        endExpiredSession(data.session.user.id);
        setLoading(false);
        return;
      }
      bootIdleExpired.current = false;
      setSession(data.session ?? null);
      if (data.session?.user) loadData(data.session.user.id);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (newSession?.user && bootIdleExpired.current) {
        bootIdleExpired.current = false;
        endExpiredSession(newSession.user.id);
        return;
      }
      setSession(newSession ?? null);
      if (newSession?.user) {
        if (event === "SIGNED_IN") writeLastActivity();
        loadData(newSession.user.id);
      } else {
        setProfile(null);
        setMemberships([]);
        setCompanies({});
      }
    });
    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, [loadData, endExpiredSession]);

  // Reconnect check: the moment connectivity returns, re-verify membership and
  // company status so a deactivation issued while the device was offline cuts
  // access immediately (Step 7 guarantee), not at the next login.
  useEffect(() => {
    const onOnline = () => { if (session?.user) loadData(session.user.id); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [session, loadData]);

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) throw error;
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      // ignore network/session errors; clear local state regardless
      logWarn("[auth] signOut network error (clearing local state anyway):", e);
    }
    clearLastActivity();
    activateAttempted.current = false;
    setSession(null);
    setProfile(null);
    setMemberships([]);
    setCompanies({});
  };

  const sendPasswordReset = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  const superMembership = memberships.find((m) => m.role === "super_admin" && m.status === "active");
  const isSuperAdmin = Boolean(superMembership);
  const activeMembership = superMembership || memberships.find((m) => m.status === "active") || null;
  const role = activeMembership?.role || null;
  const activeCompanyId = activeMembership?.company_id || null;

  // Tenant isolation for offline snapshots: namespace the cache by the acting
  // company and drop cached data for companies this user no longer belongs to.
  const authedUserId = session?.user?.id || null;
  useEffect(() => {
    setCacheScope(activeCompanyId);
    if (authedUserId && memberships.length) {
      purgeStaleCompanyCaches(authedUserId, memberships.filter((m) => m.status === "active").map((m) => m.company_id));
    }
  }, [activeCompanyId, authedUserId, memberships]);

  // -------- Two-tier feature access (Tier 1 entitlements + Tier 2 role access)
  const loadFeatures = useCallback(async (companyId) => {
    const [catRes, entsRes, roleRes] = await safeAll([
      supabase.from("feature_catalog").select("*").order("sort"),
      companyId
        ? supabase.from("company_features").select("feature_key,enabled").eq("company_id", companyId)
        : Promise.resolve({ data: [] }),
      companyId
        ? supabase.from("feature_role_access").select("feature_key,role,enabled").eq("company_id", companyId)
        : Promise.resolve({ data: [] }),
    ]);
    // Offline: keep the current feature map, or hydrate the last-known-good one.
    if (catRes.error || entsRes.error || roleRes.error) {
      const snap = cacheGet("shared", `features_${companyId}`);
      if (snap) {
        setFeatures((f) => f || snap.map);
        setFeatureCatalog((c) => (c.length ? c : snap.catalog || []));
      } else {
        // No snapshot (first offline visit): resolve the gate with an empty
        // map rather than spinning forever; role checks fail closed.
        setFeatures((f) => f || {});
      }
      return;
    }
    const cat = catRes.data, ents = entsRes.data, roleRows = roleRes.data;
    setFeatureCatalog(cat || []);
    const map = {};
    (cat || []).forEach((f) => { map[f.key] = { entitled: f.default_enabled, roles: {} }; });
    (ents || []).forEach((e) => { if (map[e.feature_key]) map[e.feature_key].entitled = e.enabled; });
    (roleRows || []).forEach((r) => { if (map[r.feature_key]) map[r.feature_key].roles[r.role] = r.enabled; });
    setFeatures(map);
    if (companyId) cachePut("shared", `features_${companyId}`, { map, catalog: cat || [] });
  }, []);

  useEffect(() => {
    if (!session?.user) { setFeatures(null); return; }
    if (dataLoading) return; // wait until memberships resolve so activeCompanyId is real
    loadFeatures(activeCompanyId);
  }, [session, activeCompanyId, dataLoading, loadFeatures]);

  // Live updates: DB triggers broadcast on the company's private features topic,
  // so entitlement/role changes apply without a reload or redeploy.
  useEffect(() => {
    if (!session?.user || !activeCompanyId) return;
    let channel;
    (async () => {
      try {
        await supabase.realtime.setAuth();
        channel = supabase
          .channel(`features:${activeCompanyId}`, { config: { private: true } })
          .on("broadcast", { event: "features" }, () => loadFeatures(activeCompanyId))
          .subscribe();
      } catch (e) { logWarn("[features] realtime subscribe failed:", e); }
    })();
    return () => { if (channel) supabase.removeChannel(channel); };
  }, [session, activeCompanyId, loadFeatures]);

  const hasFeature = useCallback((key) => {
    if (isSuperAdmin) return true;
    const f = features?.[key];
    if (!f || !f.entitled) return false;
    if (role === "org_admin") return true;
    return f.roles[role] !== false; // Tier 2 default: visible unless turned off
  }, [features, isSuperAdmin, role]);

  const activeCompany = activeCompanyId ? companies[activeCompanyId] || null : null;

  // Cache the signed-in user's ROLE-based idle limit so the boot expiry check
  // knows it before memberships load on the next launch.
  useEffect(() => {
    if (!session?.user || dataLoading) return;
    rememberIdleRole(isSuperAdmin ? "super_admin" : role);
  }, [session?.user, dataLoading, isSuperAdmin, role]);
  const companyName = activeCompany?.name || null;
  const isAssigned = isSuperAdmin || memberships.some((m) => m.status === "active");
  // A non-super user with an active membership whose company is no longer
  // accessible (archived by the Super-Admin) is treated as deactivated.
  const deactivated = !dataLoading && !isSuperAdmin && Boolean(activeMembership) && !activeCompany;

  const value = {
    session,
    user: session?.user || null,
    profile,
    memberships,
    companies,
    loading,
    dataLoading,
    role,
    isSuperAdmin,
    activeCompanyId,
    activeCompany,
    companyName,
    isAssigned,
    deactivated,
    features,
    featureCatalog,
    featuresLoading: features === null,
    hasFeature,
    reloadFeatures: () => loadFeatures(activeCompanyId),
    signIn,
    signOut,
    sendPasswordReset,
    updatePassword,
    reload: () => session?.user && loadData(session.user.id),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
